import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { checkIsBusinessUser } from "selectors/user";
import { getActiveOrganization } from "selectors/organizations";
import {
  getCurrentXeroOrganizationId,
  getSelectedBusinessClasses,
  getSelectedBusinessData,
  getSelectedBusinessLocations,
  getSelectedBusinessProjects,
  getSelectedBusinessTaxRates
} from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import Button from "nlib/ui/Button";
import Card from "nlib/common/Card";
import CategoryAndReasonInput from "nlib/common/CategoryAndReasonInput";
import Countries from "const/Countries";
import DataStatuses from "const/DataStatuses";
import ExtraDataInput from "./lib/ExtraDataInput";
import React, { useCallback, useMemo, useState } from "react";
import SelectContactInput from "nlib/common/SelectContactInput";
import Texts from "utils/Texts";
import Transactions from "utils/Transactions";
import classNames from "classnames";

const getInitialState = () => ({
  address: { name: "" },
  category: {},
  reason: "",
  class: {},
  location: {},
  project: {},
  taxRate: {}
});

const BulkActions = (props) => {
  const {
    disabled,
    simplifyLayout = false,
    className,
    selectedTransactions,
    selectedTransactionsData = [],
    usesItems,
    onCancel,
    onEdit
  } = props;

  const { uiTexts, messages } = useSelector(getTextsData);

  const classes = useSelector(getSelectedBusinessClasses);

  const locations = useSelector(getSelectedBusinessLocations);

  const projects = useSelector(getSelectedBusinessProjects);

  const taxRates = useSelector(getSelectedBusinessTaxRates);

  const businessUser = useSelector(checkIsBusinessUser);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const { countryCode } = useSelector(getActiveOrganization);

  const usCountry = countryCode === Countries.US;

  const {
    settings: {
      allowClientPayeeSelection,
      allowClientClassSelection,
      allowClientLocationSelection,
      allowClientProjectSelection,
      allowClientTaxRateSelection
    } = {}
  } = useSelector(getSelectedBusinessData);

  const [data, setData] = useState(getInitialState);

  const contactChangeValid = businessUser || Transactions.validateContactChange(data);

  const hasDataToSave = [
    data.vendorId,
    data.reason.trim(),
    data.category.code,
    data.class.id,
    data.location.id,
    data.project.id,
    data.taxRate.id
  ].some(Boolean);

  const showExtraData = ((!businessUser || allowClientClassSelection) && !!classes.length)
    || ((!businessUser || allowClientLocationSelection) && !!locations.length)
    || ((!businessUser || allowClientProjectSelection) && !!projects.length)
    || ((!businessUser || allowClientTaxRateSelection) && !usCountry && !!taxRates.length);

  const disableContactInput = businessUser && selectedTransactionsData.every(({ status }) => {
    return status !== DataStatuses.NEED_REACTION;
  });

  const transaction = useMemo(() => ({ usesItems, status: DataStatuses.NEED_REACTION }), [usesItems]);

  const bulkUpdateData = useMemo(() => {
    const reason = data.reason.trim();

    return {
      ...(data.vendorId ? {
        address: data.address,
        vendorId: data.vendorId
      } : null),
      ...(data.category.code ? {
        category: data.category,
        tags: data.tags,
        directCategorySelection: true
      } : null),
      ...(data.class.id ? { class: data.class } : null),
      ...(data.location.id ? { location: data.location } : null),
      ...(data.project.id ? { project: data.project } : null),
      ...(data.taxRate.id ? { taxRate: data.taxRate } : null),
      ...((reason && !data.category.code) ? { reason } : null)
    };
  }, [
    data.address,
    data.category,
    data.class,
    data.location,
    data.project,
    data.reason,
    data.tags,
    data.taxRate,
    data.vendorId
  ]);

  const updateData = useCallback((newData) => {
    setData((prev) => ({ ...prev, ...newData }));
  }, []);

  const handleChange = useCallback((id, newData) => updateData(newData), [updateData]);

  const handleContactChange = useCallback((value) => {
    updateData({ address: value, vendorId: value.id });
  }, [updateData]);

  const handleMarkAsCorrectClick = useCallback(() => {
    if (!contactChangeValid) return;
    onEdit({ markAsCorrect: true, ...bulkUpdateData });
  }, [contactChangeValid, bulkUpdateData, onEdit]);

  const handleEditClick = useCallback(() => {
    if (!contactChangeValid) return;
    onEdit(bulkUpdateData);
  }, [contactChangeValid, bulkUpdateData, onEdit]);

  return (
    <Card className={classNames(Css.bulkActions, className)} disabled={disabled}>
      <div className={Css.text}>
        {Texts.replaceVars(messages.editCountTransaction, { count: selectedTransactions.length })}
      </div>
      {!xeroBusiness && (
        <div className={Css.inputs}>
          {(!businessUser || allowClientPayeeSelection) && (
            <div className={Css.input}>
              <SelectContactInput
                useWidget
                valid={!!(data.address.name && data.vendorId)}
                invalid={!!(data.address.name && !data.vendorId)}
                value={data.address}
                onlyCustomers={!!usesItems}
                onChange={handleContactChange} />
            </div>
          )}
          <CategoryAndReasonInput
            className={Css.input}
            validate={!!data.category.name}
            disabled={disableContactInput}
            transaction={transaction}
            transactionState={data}
            onChange={handleChange} />
          {!simplifyLayout && showExtraData && (
            <div className={Css.input}>
              <ExtraDataInput
                disabled={disableContactInput}
                transactionState={data}
                onChange={handleChange} />
            </div>
          )}
        </div>
      )}
      <div className={Css.buttons}>
        <Button
          large
          primary={!simplifyLayout}
          danger={simplifyLayout}
          light={!simplifyLayout}
          outline={simplifyLayout}
          icon={!simplifyLayout && Icons.X}
          onClick={onCancel}>
          {simplifyLayout
            ? <Icons.X />
            : uiTexts.cancel}
        </Button>
        <Button
          large success outline
          icon={simplifyLayout ? null : Icons.ThumbsUp}
          disabled={!contactChangeValid}
          onClick={handleMarkAsCorrectClick}>
          {simplifyLayout ? <Icons.ThumbsUp /> : uiTexts.markAsCorrect}
        </Button>
        {!xeroBusiness && (
          <Button
            large primary
            icon={simplifyLayout ? null : Icons.Check}
            disabled={!hasDataToSave || disableContactInput || !contactChangeValid}
            onClick={handleEditClick}>
            {simplifyLayout ? <Icons.Check /> : uiTexts.confirmEditing}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default React.memo(BulkActions);
